import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Button } from '@/components/ui/button';
import './UserManagement.css';

const TOKEN_STORAGE_KEY = 'aipiracy_session_token';

interface ChangePasswordModalProps {
  onClose: () => void;
  force?: boolean;
  onSuccess?: () => void;
}

export default function ChangePasswordModal({ onClose, force = false, onSuccess }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const changePassword = useMutation(api.auth.changeOwnPassword);

  const handleSubmit = async (): Promise<boolean> => {
    setError('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill all fields');
      return;
    }

    if (newPassword.length < 8) {
      setError('Please choose a password with a minimum of 8 characters');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return false;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      setError('Not authenticated');
      return;
    }

    setIsLoading(true);
    try {
      await changePassword({
        token,
        currentPassword,
        newPassword,
      });

      alert('✅ Password changed successfully');
      onClose();
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const modal = (
    <div className="modal-overlay" onClick={() => { if (!force) onClose(); }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <h3>Change Password</h3>

        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '0.75rem',
            borderRadius: '6px',
            marginBottom: '1rem',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        <div className="form-group">
          <label>Current Password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div className="form-group">
          <label>New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label>Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter new password"
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                handleSubmit();
              }
            }}
          />
        </div>

        <div className="modal-actions">
          {!force && (
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
          )}
          <Button onClick={async () => { const ok = await handleSubmit(); if (ok) onSuccess?.(); }} disabled={isLoading}>
            {isLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </div>
    </div>
  );

  // Render at the document body level to escape any parent stacking contexts
  return createPortal(modal, document.body);
}
